import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Badge, Button, Tabs, Tab, Form, Spinner, Alert } from 'react-bootstrap';

const API_URL = process.env.REACT_APP_API_URL;

const AdminReport = () => {
    // State variables
    const [activeReports, setActiveReports] = useState([]);
    const [resolvedReports, setResolvedReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('active');
    
    // Selected report and action states
    const [selectedReport, setSelectedReport] = useState(null);
    const [actionNote, setActionNote] = useState('');
    const [actionType, setActionType] = useState('resolve');
    const [actionLoading, setActionLoading] = useState(false);
    const [actionSuccess, setActionSuccess] = useState(null);
    const [actionHistory, setActionHistory] = useState([]);
    const [reportDetails, setReportDetails] = useState(null);
    const [showActionForm, setShowActionForm] = useState(false);
    const [restrictionDuration, setRestrictionDuration] = useState('24'); // New state for content hiding duration

    // Action types mapped to valid enum values from schema
    const actionTypes = {
        resolve: { display: 'Resolve', status: 'resolved', variant: 'success' },
        dismiss: { display: 'Dismiss', status: 'dismissed', variant: 'danger' },
        escalate: { display: 'Escalate', status: 'in_progress', variant: 'warning' },
        inProgress: { display: 'Mark In Progress', status: 'in_progress', variant: 'primary' },
        hideContent: { display: 'Hide Content', status: 'in_progress', variant: 'secondary' } // New action
    };

    useEffect(() => {
        fetchReports();
    }, []);

    const fetchReports = async () => {
        try {
            setLoading(true);
            
            // Fetch active reports (open or in_progress)
            const activeResponse = await fetch(`${API_URL}/admin/reports/active`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            
            // Fetch resolved reports
            const resolvedResponse = await fetch(`${API_URL}/admin/reports/resolved`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            
            if (!activeResponse.ok || !resolvedResponse.ok) {
                throw new Error('Failed to fetch reports');
            }
            
            const activeData = await activeResponse.json();
            const resolvedData = await resolvedResponse.json();
            
            setActiveReports(activeData);
            setResolvedReports(resolvedData);
        } catch (err) {
            console.error('Error fetching reports:', err);
            setError(err.message);
            
            // Try fallback approach if endpoints fail
            try {
                await fetchReportsFallback();
            } catch (fallbackErr) {
                console.error('Fallback also failed:', fallbackErr);
            }
        } finally {
            setLoading(false);
        }
    };

    // Fallback if endpoints aren't implemented yet
    const fetchReportsFallback = async () => {
        try {
            const response = await fetch(`${API_URL}/tempgetreports`);
            
            if (!response.ok) {
                throw new Error('Failed to fetch reports');
            }
            
            const data = await response.json();
            
            // Split reports by status - using valid status values
            const active = data.filter(report => 
                report.status === 'open' || report.status === 'in_progress'
            );
            
            const resolved = data.filter(report => 
                report.status === 'resolved' || report.status === 'dismissed'
            );
            
            setActiveReports(active);
            setResolvedReports(resolved);
        } catch (err) {
            throw err;
        }
    };

    const handleAction = (report, action = 'resolve') => {
        setSelectedReport(report);
        setActionType(action);
        setActionNote('');
        setActionSuccess(null);
        setShowActionForm(true);
        
        // Also fetch report details when selecting a report for action
        fetchReportDetails(report);
    };

    // New handler specifically for hiding content
    const handleHideContent = (report) => {
        setSelectedReport(report);
        setActionType('hideContent');
        setRestrictionDuration('24'); // Default 24 hours
        setActionNote('');
        setActionSuccess(null);
        setShowActionForm(true);
        
        // Also fetch report details
        fetchReportDetails(report);
    };

    const submitAction = async () => {
        try {
            setActionLoading(true);
            
            if (actionType === 'hideContent') {
                // Get the content ID from the report
                let contentId = null;
                let contentType = selectedReport.content_type;
                
                // Try to extract content ID from various possible structures
                if (selectedReport.reported_content && Array.isArray(selectedReport.reported_content) 
                    && selectedReport.reported_content.length > 0) {
                    contentId = selectedReport.reported_content[0].content_id;
                } else if (reportDetails && reportDetails.reported_content) {
                    if (Array.isArray(reportDetails.reported_content)) {
                        contentId = reportDetails.reported_content[0]?.content_id;
                    } else {
                        contentId = reportDetails.reported_content.content_id;
                    }
                }
                
                if (!contentId || !contentType) {
                    throw new Error('Missing content information');
                }
                
                // Apply content restriction
                const response = await fetch(`${API_URL}/admin/content/restrict`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: JSON.stringify({
                        contentType: contentType,
                        contentId: contentId,
                        restrictionType: 'hidden',
                        reason: actionNote || 'Content hidden by moderator',
                        duration: restrictionDuration
                    })
                });
                
                if (!response.ok) {
                    throw new Error('Failed to hide content');
                }
                
                // Also update the report status
                await fetch(`${API_URL}/admin/reports/${selectedReport.case_id}/action`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: JSON.stringify({
                        action: 'inProgress',
                        notes: `Content hidden for ${restrictionDuration} hours. ${actionNote}`
                    })
                });
                
                const durationText = restrictionDuration === '0' ? 'indefinitely' : `for ${restrictionDuration} hours`;
                setActionSuccess(`Content has been hidden ${durationText}`);
            } else {
                // Original action handling for other actions
                const response = await fetch(`${API_URL}/admin/reports/${selectedReport.case_id}/action`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: JSON.stringify({
                        action: actionType,
                        notes: actionNote
                    })
                });
                
                if (!response.ok) {
                    throw new Error('Failed to update report status');
                }
                
                setActionSuccess(`Report has been ${actionTypes[actionType].display.toLowerCase()} successfully`);
            }
            
            // Refresh report list after action
            await fetchReports();
            
            // Hide action form after short delay
            setTimeout(() => {
                setShowActionForm(false);
                setActionSuccess(null);
            }, 1500);
            
        } catch (err) {
            console.error('Error taking action on report:', err);
            setActionSuccess(`Error: ${err.message}`);
        } finally {
            setActionLoading(false);
        }
    };

    const viewReportDetails = async (report) => {
        // Set the selected report and hide action form
        setSelectedReport(report);
        setShowActionForm(false);
        
        // Fetch report details
        fetchReportDetails(report);
    };
    
    const fetchReportDetails = async (report) => {
        // Log the report object to see its structure
        console.log('Report object:', report);
        
        try {
            // Fetch detailed report information including content and history
            const response = await fetch(`${API_URL}/admin/reports/${report.case_id}/details`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            
            if (!response.ok) {
                throw new Error('Failed to fetch report details');
            }
            
            const data = await response.json();
            console.log('API response data:', data);
            
            setReportDetails(data.details);
            setActionHistory(data.history || []);
            
            // Check if content is currently hidden
            await fetchContentRestrictions(report);
            
        } catch (err) {
            console.error('Error fetching report details:', err);
            // Fallback to basic details if API fails
            setReportDetails({
                description: report.description,
                content_type: report.content_type,
                category: report.category,
                reported_at: report.reported_at
            });
            setActionHistory([]);
        }
    };
    
    // New function to fetch current content restrictions
    const fetchContentRestrictions = async (report) => {
        try {
            let contentId = null;
            let contentType = report.content_type;
            
            // Try to extract content ID from report
            if (report.reported_content && Array.isArray(report.reported_content) 
                && report.reported_content.length > 0) {
                contentId = report.reported_content[0].content_id;
            } else if (report.reported_content && report.reported_content.content_id) {
                contentId = report.reported_content.content_id;
            }
            
            if (!contentId || !contentType) {
                console.warn('Cannot fetch content restrictions: missing content information');
                return;
            }
            
            const response = await fetch(`${API_URL}/admin/content/restrictions?contentType=${contentType}&contentId=${contentId}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            
            if (!response.ok) {
                console.warn('Error fetching content restrictions:', await response.text());
                return;
            }
            
            const data = await response.json();
            
            // If there are active restrictions, add them to reportDetails
            if (data && data.length > 0) {
                setReportDetails(prev => ({
                    ...prev,
                    activeRestrictions: data
                }));
            }
        } catch (err) {
            console.error('Error fetching content restrictions:', err);
        }
    };

    const getPriorityBadgeVariant = (priority) => {
        switch (priority) {
            case 1: return 'danger';
            case 2: return 'warning';
            case 3: return 'info';
            case 4: return 'secondary';
            default: return 'secondary';
        }
    };

    const getStatusBadgeVariant = (status) => {
        switch (status) {
            case 'open': return 'success';
            case 'in_progress': return 'primary';
            case 'resolved': return 'info';
            case 'dismissed': return 'danger';
            default: return 'secondary';
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Format category name for display
    const formatCategory = (category) => {
        return category
            .replace(/_/g, ' ')
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const renderReportCards = (reports) => {
        if (reports.length === 0) {
            return (
                <Col>
                    <Card className="text-center" style={{ backgroundColor: 'var(--card-background)', color: 'var(--text-primary)' }}>
                        <Card.Body>
                            <Card.Text>No reports found in this category.</Card.Text>
                        </Card.Body>
                    </Card>
                </Col>
            );
        }
        
        return reports.map(report => (
            <Col md={6} lg={4} key={report.case_id} className="mb-4">
                <Card style={{ backgroundColor: 'var(--card-background)', color: 'var(--text-primary)', boxShadow: '0 4px 8px var(--shadow-color)' }}>
                    <Card.Header className="d-flex justify-content-between align-items-center" style={{ backgroundColor: 'var(--background-secondary)', borderColor: 'var(--border-color)' }}>
                        <Badge bg={getPriorityBadgeVariant(report.priority)}>
                            Priority {report.priority}
                        </Badge>
                        <small style={{ color: 'var(--text-secondary)' }}>{formatDate(report.reported_at)}</small>
                    </Card.Header>
                    <Card.Body>
                        <Card.Title className="d-flex justify-content-between align-items-center">
                            <span>
                                {report.content_type.charAt(0).toUpperCase() + report.content_type.slice(1)} Report
                            </span>
                            <Badge bg={getStatusBadgeVariant(report.status)}>
                                {report.status.charAt(0).toUpperCase() + report.status.slice(1).replace('_', ' ')}
                            </Badge>
                        </Card.Title>
                        <Card.Subtitle className="mb-2" style={{ color: 'var(--text-secondary)' }}>
                            Category: {formatCategory(report.category)}
                        </Card.Subtitle>
                        <Card.Text style={{ 
                            maxHeight: '100px', 
                            overflow: 'auto',
                            marginBottom: '1rem',
                            color: 'var(--text-primary)'
                        }}>
                            {report.description}
                        </Card.Text>
                        
                        {activeTab === 'active' ? (
                            <div className="d-flex flex-wrap justify-content-between">
                                <Button 
                                    variant="outline-primary" 
                                    size="sm"
                                    className="mb-2 me-1"
                                    onClick={() => viewReportDetails(report)}
                                    style={{ borderColor: 'var(--text-primary)', color: 'var(--text-primary)' }}
                                >
                                    View Details
                                </Button>
                                <div className="d-flex flex-wrap">
                                    <Button 
                                        variant="outline-success" 
                                        size="sm"
                                        className="mb-2 me-1"
                                        onClick={() => handleAction(report, 'resolve')}
                                        style={{ borderColor: '#28a745', color: '#28a745' }}
                                    >
                                        Resolve
                                    </Button>
                                    <Button 
                                        variant="outline-secondary" 
                                        size="sm"
                                        className="mb-2 me-1"
                                        onClick={() => handleHideContent(report)}
                                        style={{ borderColor: '#6c757d', color: '#6c757d' }}
                                    >
                                        Hide Content
                                    </Button>
                                    <Button 
                                        variant="outline-warning" 
                                        size="sm"
                                        className="mb-2"
                                        onClick={() => handleAction(report, 'escalate')}
                                        style={{ borderColor: '#ffc107', color: '#ffc107' }}
                                    >
                                        Escalate
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <Button 
                                variant="outline-primary" 
                                size="sm"
                                onClick={() => viewReportDetails(report)}
                                style={{ borderColor: 'var(--text-primary)', color: 'var(--text-primary)' }}
                            >
                                View History
                            </Button>
                        )}
                    </Card.Body>
                </Card>
            </Col>
        ));
    };

    // Component to display content restriction status
    const ContentRestrictionStatus = ({ restriction }) => {
        const now = new Date();
        const expiresAt = restriction.expires_at ? new Date(restriction.expires_at) : null;
        const isExpired = expiresAt && expiresAt < now;
        
        return (
            <Card className="mb-3" style={{ 
                backgroundColor: 'rgba(220, 53, 69, 0.1)', 
                borderColor: '#dc3545' 
            }}>
                <Card.Body>
                    <div className="d-flex justify-content-between">
                        <h5 style={{ color: '#dc3545' }}>
                            Content is {isExpired ? 'was' : ''} hidden
                        </h5>
                        <Badge bg={isExpired ? 'secondary' : 'danger'}>
                            {isExpired ? 'Expired' : 'Active'}
                        </Badge>
                    </div>
                    <p>Reason: {restriction.reason}</p>
                    {expiresAt && (
                        <p>
                            {isExpired 
                                ? `Was hidden until ${formatDate(restriction.expires_at)}` 
                                : `Hidden until ${formatDate(restriction.expires_at)}`}
                        </p>
                    )}
                    {!expiresAt && (
                        <p>Hidden indefinitely</p>
                    )}
                    <p className="mb-0 text-muted">
                        Applied by Admin ID: {restriction.applied_by} on {formatDate(restriction.applied_at)}
                    </p>
                </Card.Body>
            </Card>
        );
    };

    if (loading) return (
        <div className="text-center p-5" style={{ color: 'var(--text-primary)' }}>
            <Spinner animation="border" role="status" style={{ color: 'var(--text-primary)' }} />
            <h3 className="mt-3">Loading reports...</h3>
        </div>
    );
    
    if (error) return (
        <Alert variant="danger" style={{ backgroundColor: 'rgba(220, 53, 69, 0.2)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}>
            <Alert.Heading>Error loading reports</Alert.Heading>
            <p>{error}</p>
        </Alert>
    );

    // Render the details panel when a report is selected
    const renderDetailsPanel = () => {
        if (!selectedReport) {
            return (
                <Card style={{ backgroundColor: 'var(--card-background)', color: 'var(--text-primary)', height: '100%' }}>
                    <Card.Body className="d-flex justify-content-center align-items-center">
                        <p className="text-center">Select a report to view details</p>
                    </Card.Body>
                </Card>
            );
        }

        if (showActionForm) {
            return (
                <Card style={{ backgroundColor: 'var(--card-background)', color: 'var(--text-primary)', height: '100%' }}>
                    <Card.Header style={{ backgroundColor: 'var(--background-secondary)', borderColor: 'var(--border-color)' }}>
                        <div className="d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">{actionTypes[actionType].display} Report</h5>
                            <Button 
                                variant="link" 
                                onClick={() => setSelectedReport(null)}
                                style={{ color: 'var(--text-primary)', textDecoration: 'none' }}
                            >
                                ×
                            </Button>
                        </div>
                    </Card.Header>
                    <Card.Body style={{ overflowY: 'auto' }}>
                        {actionSuccess ? (
                            <Alert variant={actionSuccess.includes('Error') ? 'danger' : 'success'} className="mb-0">
                                {actionSuccess}
                            </Alert>
                        ) : (
                            <>
                                <p>You are about to {actionTypes[actionType].display.toLowerCase()} the following report:</p>
                                <Card className="mb-3" style={{ backgroundColor: 'var(--background-secondary)' }}>
                                    <Card.Body>
                                        <p className="mb-1"><strong>Type:</strong> {selectedReport?.content_type}</p>
                                        <p className="mb-1"><strong>Category:</strong> {selectedReport?.category && formatCategory(selectedReport.category)}</p>
                                        <p className="mb-0"><strong>Description:</strong> {selectedReport?.description}</p>
                                    </Card.Body>
                                </Card>
                                
                                {actionType === 'hideContent' && (
                                    <Form.Group className="mb-3">
                                        <Form.Label>Hide Duration (hours)</Form.Label>
                                        <Form.Select
                                            value={restrictionDuration}
                                            onChange={(e) => setRestrictionDuration(e.target.value)}
                                            style={{ 
                                                backgroundColor: 'var(--input-background)', 
                                                color: 'var(--text-primary)',
                                                borderColor: 'var(--border-color)'
                                            }}
                                        >
                                            <option value="1">1 hour</option>
                                            <option value="4">4 hours</option>
                                            <option value="12">12 hours</option>
                                            <option value="24">24 hours</option>
                                            <option value="48">48 hours</option>
                                            <option value="72">72 hours</option>
                                            <option value="168">1 week</option>
                                            <option value="720">30 days</option>
                                            <option value="0">Indefinite</option>
                                        </Form.Select>
                                    </Form.Group>
                                )}
                                
                                <Form.Group className="mb-3">
                                    <Form.Label>Action Notes</Form.Label>
                                    <Form.Control 
                                        as="textarea" 
                                        rows={3} 
                                        value={actionNote}
                                        onChange={(e) => setActionNote(e.target.value)}
                                        placeholder={actionType === 'hideContent' 
                                            ? "Reason for hiding content (will be shown to users)" 
                                            : "Add notes about this action..."}
                                        style={{ 
                                            backgroundColor: 'var(--input-background)', 
                                            color: 'var(--text-primary)',
                                            borderColor: 'var(--border-color)'
                                        }}
                                    />
                                </Form.Group>
                                
                                <div className="d-flex justify-content-end">
                                    <Button 
                                        variant="secondary" 
                                        onClick={() => setShowActionForm(false)}
                                        className="me-2"
                                        style={{ backgroundColor: 'var(--background-secondary)', borderColor: 'var(--border-color)' }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button 
                                        variant={actionTypes[actionType].variant}
                                        onClick={submitAction}
                                        disabled={actionLoading}
                                    >
                                        {actionLoading ? (
                                            <>
                                                <Spinner
                                                    as="span"
                                                    animation="border"
                                                    size="sm"
                                                    role="status"
                                                    aria-hidden="true"
                                                    className="me-2"
                                                />
                                                Processing...
                                            </>
                                        ) : (
                                            <>Confirm {actionTypes[actionType].display}</>
                                        )}
                                    </Button>
                                </div>
                            </>
                        )}
                    </Card.Body>
                </Card>
            );
        }

        return (
            <Card style={{ backgroundColor: 'var(--card-background)', color: 'var(--text-primary)', height: '100%' }}>
                <Card.Header style={{ backgroundColor: 'var(--background-secondary)', borderColor: 'var(--border-color)' }}>
                    <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">Report Details</h5>
                        <Button 
                            variant="link" 
                            onClick={() => setSelectedReport(null)}
                            style={{ color: 'var(--text-primary)', textDecoration: 'none' }}
                        >
                            ×
                        </Button>
                    </div>
                </Card.Header>
                <Card.Body style={{ overflowY: 'auto' }}>
                    {reportDetails ? (
                        <div>
                            {/* Display active restriction if any */}
                            {reportDetails.activeRestrictions && reportDetails.activeRestrictions.length > 0 && (
                                <ContentRestrictionStatus restriction={reportDetails.activeRestrictions[0]} />
                            )}
                            
                            <h5 style={{ color: 'var(--text-primary)' }}>Report Information</h5>
                            <Row className="mb-4">
                                <Col md={6}>
                                    <p><strong>Report ID:</strong> {selectedReport?.case_id}</p>
                                    <p><strong>Content Type:</strong> {selectedReport?.content_type}</p>
                                    <p><strong>Category:</strong> {selectedReport?.category && formatCategory(selectedReport.category)}</p>
                                    <p><strong>Status:</strong> {selectedReport?.status}</p>
                                </Col>
                                <Col md={6}>
                                    <p><strong>Priority:</strong> {selectedReport?.priority}</p>
                                    <p><strong>Reported:</strong> {formatDate(selectedReport?.reported_at)}</p>
                                    <p><strong>Content ID:</strong> {(() => {
                                        // First try to extract from any available structure
                                        let contentId = null;
                                        
                                        // Try to get from selectedReport.reported_content array
                                        if (selectedReport?.reported_content && Array.isArray(selectedReport.reported_content) && 
                                            selectedReport.reported_content.length > 0) {
                                            contentId = selectedReport.reported_content[0]?.content_id;
                                        }
                                        
                                        // If not found, try other potential structures
                                        if (!contentId && selectedReport?.reported_content?.content_id) {
                                            contentId = selectedReport.reported_content.content_id;
                                        }
                                        
                                        // Try from reportDetails
                                        if (!contentId && reportDetails?.reported_content) {
                                            if (Array.isArray(reportDetails.reported_content)) {
                                                contentId = reportDetails.reported_content[0]?.content_id;
                                            } else {
                                                contentId = reportDetails.reported_content.content_id;
                                            }
                                        }
                                        
                                        // Try fallback options
                                        if (!contentId) {
                                            contentId = selectedReport?.reported_content_id || 
                                                        reportDetails?.reported_content_id || 
                                                        reportDetails?.content_id;
                                        }
                                        
                                        return contentId || 'Unknown';
                                    })()}</p>
                                </Col>
                            </Row>
                            
                            <h5 style={{ color: 'var(--text-primary)' }}>Description</h5>
                            <Card className="mb-4" style={{ backgroundColor: 'var(--background-secondary)' }}>
                                <Card.Body>
                                    {selectedReport?.description || 'No description provided'}
                                </Card.Body>
                            </Card>
                            
                            <h5 style={{ color: 'var(--text-primary)' }}>Action History</h5>
                            {actionHistory.length === 0 ? (
                                <p>No actions have been taken on this report yet.</p>
                            ) : (
                                actionHistory.map((action, index) => (
                                    <Card key={index} className="mb-2" style={{ backgroundColor: 'var(--background-secondary)' }}>
                                        <Card.Body>
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div>
                                                    <p className="mb-1"><strong>{action.update_type}</strong></p>
                                                    <p className="mb-0">{action.update_details}</p>
                                                </div>
                                                <div className="text-end">
                                                    <small>{formatDate(action.created_at)}</small>
                                                    <p className="mb-0"><small>By: {action.admin_name || 'System'}</small></p>
                                                </div>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                ))
                            )}
                            
                            {(selectedReport?.status === 'open' || selectedReport?.status === 'in_progress') && (
                                <div className="d-flex flex-wrap justify-content-end mt-3">
                                    <Button 
                                        variant="outline-primary" 
                                        className="me-2 mb-2"
                                        onClick={() => handleAction(selectedReport, 'inProgress')}
                                        style={{ borderColor: '#0d6efd', color: '#0d6efd' }}
                                    >
                                        Mark In Progress
                                    </Button>
                                    <Button 
                                        variant="outline-secondary" 
                                        className="me-2 mb-2"
                                        onClick={() => handleHideContent(selectedReport)}
                                        style={{ borderColor: '#6c757d', color: '#6c757d' }}
                                    >
                                        Hide Content
                                    </Button>
                                    <Button 
                                        variant="outline-success" 
                                        className="me-2 mb-2"
                                        onClick={() => handleAction(selectedReport, 'resolve')}
                                        style={{ borderColor: '#28a745', color: '#28a745' }}
                                    >
                                        Resolve
                                    </Button>
                                    <Button 
                                        variant="outline-danger"
                                        className="mb-2"
                                        onClick={() => handleAction(selectedReport, 'dismiss')}
                                        style={{ borderColor: '#dc3545', color: '#dc3545' }}
                                    >
                                        Dismiss Report
                                    </Button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-center py-4">
                            <Spinner animation="border" />
                            <p className="mt-2">Loading report details...</p>
                        </div>
                    )}
                </Card.Body>
            </Card>
        );
    };

    // Content Unavailable component for user-facing display
    const ContentUnavailable = ({ reason, expiresAt }) => {
        return (
            <div className="content-unavailable-container">
                <Card className="text-center">
                    <Card.Body>
                        <div className="mb-4">
                            <span className="moderation-icon">
                                <i className="fa fa-exclamation-circle" style={{ fontSize: '3rem', color: '#dc3545' }}></i>
                            </span>
                        </div>
                        <Card.Title>Content Temporarily Unavailable</Card.Title>
                        <Card.Text>
                            This content has been hidden by moderators.
                            {reason && <p className="mt-2">{reason}</p>}
                            {expiresAt && (
                                <p className="text-muted mt-2">
                                    This content will be available again after {formatDate(expiresAt)}.
                                </p>
                            )}
                        </Card.Text>
                    </Card.Body>
                </Card>
            </div>
        );
    };

    return (
        <div>
            <h2 className="mb-4" style={{ color: 'var(--text-primary)' }}>Reports Management</h2>
            
            <Row>
                {/* Left column - Reports list */}
                <Col md={selectedReport ? 7 : 12}>
                    <Tabs
                        activeKey={activeTab}
                        onSelect={(k) => setActiveTab(k)}
                        className="mb-4"
                        style={{ borderBottom: '1px solid var(--border-color)' }}
                    >
                        <Tab 
                            eventKey="active" 
                            title={`Active Reports (${activeReports.length})`}
                            tabClassName="text-primary"
                        >
                            <Row>
                                {renderReportCards(activeReports)}
                            </Row>
                        </Tab>
                        <Tab 
                            eventKey="resolved" 
                            title={`Resolved Reports (${resolvedReports.length})`}
                            tabClassName="text-success"
                        >
                            <Row>
                                {renderReportCards(resolvedReports)}
                            </Row>
                        </Tab>
                    </Tabs>
                </Col>
                
                {/* Right column - Details panel */}
                {selectedReport && (
                    <Col md={5} style={{ position: 'sticky', top: '20px', height: 'calc(100vh - 100px)' }}>
                        {renderDetailsPanel()}
                    </Col>
                )}
            </Row>
        </div>
    );
};

export default AdminReport;